// Charms (docs/05 §2): Common, Uncommon, Rare, Legendary and † Cursed. Charms whose system does
// not exist yet declare `requires` and are never offered until it does.
import { D, ZERO } from '../sim/decimal';
import { addTo } from '../sim/util';
import { B } from './balance';
import { defineCharm, isLike, type CharmDef } from './defs';

const eyes = (m: { features: Set<string> }) => m.features.has('eyes');
const face = (m: { features: Set<string> }) => m.features.has('face');
const rot = (m: { symbolGates: Set<string> }) => m.symbolGates.has('rot');
const mulId = (m: { valueXById: Record<string, number> }, id: string, x: number) => void (m.valueXById[id] = (m.valueXById[id] ?? 1) * x);

const common: CharmDef[] = [
  defineCharm({ id: 'penny', rarity: 'C', mods: (m) => void (m.luck += 1) }),
  defineCharm({ id: 'sugar', rarity: 'C', mods: (m) => addTo(m.valueByTag, 'fruit', 2) }),
  defineCharm({ id: 'knuckle', rarity: 'C', mods: (m) => addTo(m.valueByTag, 'bar', 3) }),
  defineCharm({ id: 'sparehand', rarity: 'C', requires: (m) => m.features.has('hold'), mods: (m) => void (m.holdPerWindow += 1) }),
  defineCharm({ id: 'grease', rarity: 'C', requires: (m) => m.features.has('nudge'), mods: (m) => void (m.nudgePerWindow += 1) }),
  defineCharm({ id: 'receipt', rarity: 'C', mods: (m) => void (m.payEarlyPct += 0.1) }),
  defineCharm({ id: 'doggybag', rarity: 'C', requires: (m) => m.features.has('feed'), mods: (m) => void (m.feedValuePar += 1) }),
  defineCharm({ id: 'saltcircle', rarity: 'C', requires: (m) => m.symbolGates.has('hex') }),
  defineCharm({ id: 'preserves', rarity: 'C', requires: (m) => m.symbolGates.has('rot'), mods: (m) => void (m.rotSlow *= 2) }),
  defineCharm({ id: 'pocketwatch', rarity: 'C', mods: (m) => void (m.spinsPerWindow += 1) }),
  defineCharm({
    id: 'pairdice',
    rarity: 'C',
    requires: (m) => m.patterns.has('pair'),
    mods: (m) => addTo(m.patternMultAdd, 'pair', 0.5) }),
  defineCharm({ id: 'rubberband', rarity: 'C', spinLuck: (c) => (c.spinOfWindow === 0 ? 3 : 0) }),
  defineCharm({ id: 'gum', rarity: 'C', requires: (m) => m.features.has('face'), mods: (m) => void (m.hungerGainPct -= 0.25) }),
  defineCharm({
    id: 'piggy',
    rarity: 'C',
    onBank: (c) => {
      if (c.payout.gt(0)) c.store('bank', c.payout.mul(0.05));
    },
    onWindowEnd: (c) => {
      const stored = c.take('bank');
      return stored.gt(ZERO) ? stored.mul(D(2)).floor() : null;
    },
  }),
  defineCharm({ id: 'thimble', rarity: 'C', mods: (m) => addTo(m.valueById, 'coin', 2) }),
];

const uncommon: CharmDef[] = [
  defineCharm({ id: 'magnifier', rarity: 'U', mods: (m) => void (m.peekRows += 1) }),
  defineCharm({ id: 'glasseye', rarity: 'U', requires: eyes, mods: (m) => void (m.eyeCountX = 2) }),
  defineCharm({
    id: 'flypaper',
    rarity: 'U',
    requires: rot,
    mods: (m) => {
      addTo(m.valueById, 'fly', 3);
      m.noEat.add('fly');
    },
  }),
  defineCharm({ id: 'rosary', rarity: 'U', requires: eyes, mods: (m) => void (m.devourChanceX *= 0.5) }),
  defineCharm({
    id: 'dentures',
    rarity: 'U',
    requires: eyes,
    mods: (m) => {
      mulId(m, 'tooth', 2);
      m.devourTeeth = Math.max(m.devourTeeth, 4);
    },
  }),
  defineCharm({
    id: 'bonesaw',
    rarity: 'U',
    runMods: (m, c) => void (c.destroyed > 0 && m.xMult.push({ source: 'bonesaw', x: 1 + B.BONESAW_X * c.destroyed })),
  }),
  defineCharm({ id: 'metronome', rarity: 'U', mods: (m) => void (m.metronome = true) }),
  defineCharm({ id: 'snakeoil', rarity: 'U', requires: rot, mods: (m) => void (m.snakeOil = true) }),
  defineCharm({ id: 'catbell', rarity: 'U', requires: rot, mods: (m) => void (m.catReach = Math.max(m.catReach, 2)) }),
  defineCharm({ id: 'hymnbook', rarity: 'U', requires: (m) => m.patterns.has('diag3'), mods: (m) => addTo(m.patternMultAdd, 'diag3', 1) }),
  defineCharm({ id: 'loadeddie', rarity: 'U', mods: (m) => void (m.diceMin = Math.max(m.diceMin, 4)) }),
  defineCharm({ id: 'ledger', rarity: 'U', mods: (m) => void (m.soulFlatPerTribute += 1) }),
  defineCharm({ id: 'mothlamp', rarity: 'U', requires: face, mods: (m) => void (m.mothLamp = true) }),
  defineCharm({ id: 'fishhook', rarity: 'U', requires: (m) => m.features.has('nudge'), mods: (m) => void (m.deadSpinNudge += 1) }),
  defineCharm({ id: 'tipjar', rarity: 'U', mods: (m) => void (m.windowStartCoinsPct += 0.05) }),
  defineCharm({
    id: 'wishbone',
    rarity: 'U',
    requires: face,
    mods: (m) => {
      addTo(m.valueByTag, 'bone', 2);
      if (!m.extraWildFor.includes('rat')) m.extraWildFor.push('rat');
    },
  }),
  defineCharm({ id: 'lighter', rarity: 'U', requires: face, mods: (m) => void (m.zippo = true) }),
];

const rare: CharmDef[] = [
  defineCharm({ id: 'goldengrin', rarity: 'R', mods: (m) => void (m.grinPatternX *= 2) }),
  defineCharm({
    id: 'secondmouth',
    rarity: 'R',
    requires: (m) => m.features.has('feed'),
    mods: (m) => {
      m.feedX *= 2;
      m.feedDraftBonus = true;
    },
  }),
  defineCharm({ id: 'crookedlever', rarity: 'R', mods: (m) => void (m.perfectPullXMult *= 1.5) }),
  defineCharm({ id: 'surgeonkit', rarity: 'R', requires: (m) => m.features.has('feed'), mods: (m) => void (m.feedGrow += 2) }),
  defineCharm({
    id: 'sandpendant',
    rarity: 'R',
    requires: face,
    mods: (m) => {
      m.hourglassSpins = Math.max(m.hourglassSpins, 2);
      m.hourglassMax = Math.max(m.hourglassMax, 4);
    },
  }),
  defineCharm({ id: 'voodoo', rarity: 'R', requires: (m) => m.symbolGates.has('hex'), mods: (m) => void (m.voodoo = true) }),
  defineCharm({ id: 'crystalball', rarity: 'R', mods: (m) => void (m.crystalBall = true) }),
  defineCharm({
    id: 'ratking',
    rarity: 'R',
    requires: rot,
    mods: (m) => {
      addTo(m.valueById, 'rat', 2);
      m.ratsWild = true;
    },
  }),
  defineCharm({
    id: 'bloodbank',
    rarity: 'R',
    requires: (m) => m.features.has('nudge'),
    mods: (m) => void (m.bloodBank = true),
    runMods: (m, c) => void ((c.flags.bloodBankX ?? 0) > 0 && m.xMult.push({ source: 'bloodbank', x: 1 + c.flags.bloodBankX! })),
  }),
  defineCharm({ id: 'recipe', rarity: 'R', requires: (m) => m.bonuses.has('feast'), mods: (m) => void (m.feastRespins = Math.max(m.feastRespins, 4)) }),
  defineCharm({ id: 'visor', rarity: 'R', requires: (m) => m.bonuses.has('hilo'), mods: (m) => void (m.hiloVisor = true) }),
  defineCharm({ id: 'pinball', rarity: 'R', requires: (m) => m.bonuses.has('plinko'), mods: (m) => void (m.plinkoBalls += 2) }),
  defineCharm({ id: 'wheelgrease', rarity: 'R', requires: (m) => m.bonuses.has('wheel'), mods: (m) => void (m.wheelBand *= 2) }),
  defineCharm({ id: 'scratchcoin', rarity: 'R', requires: (m) => m.bonuses.has('scratch'), mods: (m) => void (m.scratchGlow = true) }),
  defineCharm({ id: 'anchor', rarity: 'R', requires: (m) => m.features.has('hold'), mods: (m) => void (m.heldPatternMult += 1) }),
];

const legendary: CharmDef[] = [
  defineCharm({
    id: 'mawsfavor',
    rarity: 'L',
    requires: face,
    evalXMult: (c) => (c.mood === 'ravenous' || c.mood === 'starving' ? 3 : 1),
  }),
  defineCharm({ id: 'halofrag', rarity: 'L', requires: eyes, mods: (m) => void (m.halofrag = true) }),
  defineCharm({ id: 'infreceipt', rarity: 'L', mods: (m) => void (m.carryX *= B.INF_RECEIPT_CARRY) }),
  defineCharm({ id: 'heartmachine', rarity: 'L', requires: face, mods: (m) => void (m.hungerCap = Math.min(m.hungerCap, B.HEART_MACHINE_CAP)) }),
  defineCharm({
    id: 'seventhseal',
    rarity: 'L',
    evalXMult: (c) => Math.pow(B.SEVENTH_SEAL_X, c.grid.all().filter((v) => isLike(v.def, 'seven')).length),
  }),
];

/** † Cursed: never in the regular shop; a Reckoning's shop offers one. */
const cursed: CharmDef[] = [
  defineCharm({
    id: 'c_bloodnail',
    rarity: 'R',
    cursed: true,
    mods: (m) => {
      m.xMult.push({ source: 'c_bloodnail', x: 2 });
      m.spinCoinCost += B.BLOODNAIL_COST;
    },
  }),
  defineCharm({
    id: 'c_hungrycoin',
    rarity: 'R',
    cursed: true,
    requires: face,
    mods: (m) => {
      mulId(m, 'coin', 5);
      m.hungerPerSpin += 3;
    },
  }),
  defineCharm({
    id: 'c_blindfold',
    rarity: 'R',
    cursed: true,
    mods: (m) => {
      m.luck += 4;
      m.peekRows = 0;
      m.blind = true;
    },
  }),
];

export const CHARM_LIST: CharmDef[] = [...common, ...uncommon, ...rare, ...legendary, ...cursed];
